import React, {Fragment, useState} from 'react';
import {browserHistory, Link} from 'react-router';
import {List, Map} from 'immutable';
import Tooltip from '@material-ui/core/Tooltip';
import {
  KpiAssignment,
  KpiAssignmentId,
  KpiOrderBy,
  KpiTarget,
  KpiTargetId,
  KpiTargetTree,
} from '@store/@types/kpi';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import {CircledPlusIcon} from '@components/icon/CircledPlusIcon';
import './AssignmentList.styl';
import {KpiBulbIcon} from '@components/icon/KpiBulbIcon';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import {BullseyeIcon} from '@components/icon/BullseyeIcon';
import MenuItem from '@material-ui/core/MenuItem';
import confirm from 'utils/confirm';
import CogIcon from 'components/pure/utils/icon/CogIcon';
import Menu from '@material-ui/core/Menu';
import MenuList from '@material-ui/core/MenuList';
import {KpiHeader} from './KpiHeader';

interface AssignmentListProps {
  completedAssignments: Map<KpiAssignmentId, boolean>;
  assignmentsByKpiId: Map<KpiTargetId, List<KpiAssignment>>;
  kpiTargetTree: List<KpiTargetTree>;
  refreshData: () => Promise<unknown>;
  direction: 'asc' | 'desc';
  orderBy: KpiOrderBy | undefined;
  setOrderBy: (o: KpiOrderBy) => void;
}

const HEADER_NAME = 'name' as const;
const HEADER_ASSIGNMENT = 'assignments' as const;

const COMPLETE_CONFIRMATION =
  'Are you sure you want to mark this as complete? This will stop the system from prompting the assignee to provide updates.';

export const AssignmentList: React.FC<AssignmentListProps> = ({
  assignmentsByKpiId,
  completedAssignments,
  kpiTargetTree,
  direction,
  orderBy,
  setOrderBy,
  refreshData,
}) => {
  const getSortedData = () => {
    const data =
      orderBy === HEADER_ASSIGNMENT
        ? kpiTargetTree
            .sortBy(({kpiTarget}) => assignmentsByKpiId.get(kpiTarget.id, List()).size)
            .reverse()
        : kpiTargetTree.sortBy(({kpiTarget}) => kpiTarget.name);
    return direction === 'asc' ? data : data.reverse();
  };

  return (
    <table className="assignment-list">
      <thead>
        <tr>
          <KpiHeader
            className="goal-kpi"
            name={HEADER_NAME}
            setOrder={setOrderBy}
            orderBy={orderBy}
            direction={direction}>
            Goal / KPI
          </KpiHeader>
          <KpiHeader
            className="assigned-to"
            name={HEADER_ASSIGNMENT}
            setOrder={setOrderBy}
            orderBy={orderBy}
            direction={direction}>
            Assigned to
          </KpiHeader>
        </tr>
      </thead>
      <tbody>
        {getSortedData().map(({kpiTarget, kpiChildren}) => {
          const isGoal = !kpiChildren.isEmpty();
          return (
            <Fragment key={kpiTarget.id}>
              <KpiRow
                key={kpiTarget.id}
                kpiTarget={kpiTarget}
                isGoal={isGoal}
                assignmentsByKpiId={assignmentsByKpiId}
                refreshData={refreshData}
                completedAssignments={completedAssignments}
              />
              {kpiChildren.map(child => (
                <KpiRow
                  key={child.id}
                  kpiTarget={child}
                  isGoal={false}
                  assignmentsByKpiId={assignmentsByKpiId}
                  refreshData={refreshData}
                  completedAssignments={completedAssignments}
                />
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

interface RowProps {
  kpiTarget: KpiTarget;
  isGoal: boolean;
  assignmentsByKpiId: Map<KpiTargetId, List<KpiAssignment>>;
  refreshData: () => Promise<unknown>;
  completedAssignments: Map<KpiAssignmentId, boolean>;
}

const KpiRow: React.FC<RowProps> = ({
  kpiTarget,
  isGoal,
  assignmentsByKpiId,
  refreshData,
  completedAssignments,
}) => {
  const [menuRef, setMenuRef] = useState<Element | null>(null);
  const [currId, setCurrId] = useState<KpiAssignmentId | null>(null);

  return (
    <tr key={kpiTarget.id}>
      <td className="name-cell">
        <div className="parent">
          <Link
            to={{
              pathname: `/admin/kpis/edit/${kpiTarget.id}`,
              query: {tabIndex: 1},
            }}>
            <span className="name">
              <GenericTooltip inline tooltip={isGoal ? 'Goal' : 'KPI'}>
                {isGoal ? <BullseyeIcon /> : <KpiBulbIcon />}
              </GenericTooltip>
              {kpiTarget.name}
            </span>
          </Link>
          <Tooltip title="Assign a Goal/KPI to...">
            <span>
              <CircledPlusIcon
                onClick={() => browserHistory.push(`/admin/kpis/assign/${kpiTarget.id}`)}
              />
            </span>
          </Tooltip>
        </div>
      </td>
      <td className="assignee-cell">
        {assignmentsByKpiId.get(kpiTarget.id, List()).map(assignment => {
          const completed = completedAssignments.get(assignment.id, false);
          return (
            <div key={assignment.id} className="assignee-name-row">
              <Link
                to={{
                  pathname: `/admin/kpis/assign/${kpiTarget.id}/edit/${assignment.id}`,
                  query: {tabIndex: 1},
                }}>
                {assignment.assignee.name}
              </Link>
              <div className="options-wrapper">
                {completed && <div className="complete-pill">Complete</div>}
                <CogIcon
                  onClick={e => {
                    setCurrId(assignment.id);
                    setMenuRef(e.currentTarget);
                  }}
                />
                <Menu
                  onClose={() => setCurrId(null)}
                  anchorEl={menuRef}
                  open={currId === assignment.id}
                  className="action-menu">
                  <MenuList dense disablePadding>
                    <MenuItem
                      disabled={false}
                      onClick={() =>
                        confirm(COMPLETE_CONFIRMATION)
                          .then(() => KpiGoalActions.forceCompleteAssignment(assignment.id))
                          .then(refreshData)
                          .catch(console.error)
                          .finally(() => setCurrId(null))
                      }>
                      Complete
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        KpiGoalActions.deleteAssignment(assignment.id)
                          .then(refreshData)
                          .catch(console.error)
                      }>
                      Delete
                    </MenuItem>
                  </MenuList>
                </Menu>
              </div>
            </div>
          );
        })}
      </td>
    </tr>
  );
};
